import { gameEnDictionary } from "./localization/game.en";
import { createGameAudioOverrides } from "./audio/createGameAudioOverrides";
import { INTRO_SPLASH_SPEC } from "./info/IntroSplashSpec";
import { buildPaytableTokens } from "./info/PaytableSpec";
import { buildFeaturePurchaseInfoTokens } from "./info/FeaturePurchaseInfoTokens";
import { createFeaturePurchaseOffers } from "./feature/createFeaturePurchaseOffers";
import { FIXTURES, createFixtureCatalog } from "./sim/fixtures";
import { HISTORY_FIXTURE } from "./sim/historyFixture";
import { BIG_WIN_TIERS, FEATURE_MODE_MULTIPLIERS, FEATURE_PRICE_MULTIPLIERS } from "./sim/teddyRules";
import { BOOT_FALLBACK_GRID } from "./composition/bootFallbackGrid";
import { SPEED_MODE_SCALES } from "./tuning/speedModeTuning";
import { createScene, type BloodyTeddyScene } from "./scene/createScene";
import { createPresentation } from "./composition/createPresentation";
import { SELECTABLE_FIXTURES } from "./sim/fixtureRegistry";
import { buildFilterTestScene } from "./dev/filterTestScene";
import { version } from "../package.json";
import { FixtureGameHistoryProvider, FixtureReplayRoundProvider, FixtureRoundProvider, loadLocalizationDictionaryFromBundle, type GameDefinition } from "@slot/game-shell";

/**
 * Bloody Teddy as one object (plan §2.2): what the shell's boot, chrome and offline data take from this title, and the
 * factories for its scene and its round presentation.
 */
export const bloodyTeddy: GameDefinition<BloodyTeddyScene> = {
  id: "bloody-teddy",
  version,
  grid: { columns: 5, rows: 5 },
  speedModeScales: SPEED_MODE_SCALES,
  bootFallbackGrid: BOOT_FALLBACK_GRID,

  boot: {
    locale: {
      baseDictionary: gameEnDictionary,
      loadRemoteDictionary: ({ locale, gameBundleRegistry }) => loadLocalizationDictionaryFromBundle(gameBundleRegistry, `localization/game.${locale}`),
    },
    // Offline only: the live server's settings for this game (`/api/initial-settings`): its title, no gamble, the
    // buys' and the modes' prices, the big-win tiers.
    fixtureSettingsOverrides: {
      gameTitle: "Bloody Teddy",
      gambleEnabled: false,
      featurePriceMultipliersById: { ...FEATURE_PRICE_MULTIPLIERS },
      featureModeMultipliersById: { ...FEATURE_MODE_MULTIPLIERS },
      bigWinTiers: BIG_WIN_TIERS.map((tier) => ({ ...tier })),
    },
    // The shell loads its core set (`shared-core`, `game-shared-visuals`, `shell-ui`) itself; this title has no bundle of its own.
    extraBundles: [],
    audioOverrides: createGameAudioOverrides(),
  },

  chrome: {
    introSplash: INTRO_SPLASH_SPEC,
    // The info page's tokens: the paytable at the current bet, the offers' prices.
    buildExtraInfoTokens: (input) => ({
      ...buildPaytableTokens(input),
      ...buildFeaturePurchaseInfoTokens({
        featurePriceMultipliersById: input.initialSettings.featurePriceMultipliersById ?? {},
        featureModeMultipliersById: input.initialSettings.featureModeMultipliersById ?? {},
      }),
    }),
    featurePurchaseOffers: createFeaturePurchaseOffers(),
  },

  fixtures: {
    createCatalog: () => createFixtureCatalog(),
    // Offline dev + the e2e suite: the base round robin, the buys and the modes by id, or a playlist of the selectable
    // rounds (the dev menu, the hook).
    createRoundProvider: () =>
      new FixtureRoundProvider(createFixtureCatalog(), SELECTABLE_FIXTURES, {
        simulatedLatencyMs: 50,
        cycleThroughFixtures: true,
        startGroup: "base",
        rejectLowBalanceForTesting: false,
        rejectedBalanceAmount: "420",
        rejectedCurrencyCode: "EUR",
      }),
    createHistoryProvider: () => new FixtureGameHistoryProvider(HISTORY_FIXTURE, { simulatedLatencyMs: 300 }),
    // `?replay=` / `?pending=`: the VS Rush session for the history's biggest win, a line win for any other id, the
    // shell's `fail` for the failure path.
    createReplayRoundProvider: () => new FixtureReplayRoundProvider({ roundsById: { "4015571829392": FIXTURES.fs_vs_rush }, defaultRound: FIXTURES.reel_line_win }, { simulatedLatencyMs: 300 }),
  },

  createScene,
  createPresentation,
  dev: {
    createFilterTestScene: buildFilterTestScene,
  },
};
